import React from 'react';
import { generatePath } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import format from 'date-fns/format';
import { NuclearIcon } from '@/assets/images';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@/components/common/ResponsiveTable';
import RouterLink from '@/components/common/RouterLink';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import { useCurrentUser } from '@/lib/hooks/userData';
import translate from '@/lib/intl/translate';
import BulkImportStatus from '../../config/BulkImportStatus';
import BulkImportStatusName from '../../config/BulkImportStatusName';
import showProcessedStats from '../../config/showProcessedStats';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import useDestroyAllDataDialog from '../../hooks/useDestroyAllDataDialog';
import type BulkImport from '../../types/BulkImport';
import CancelDataUploadButton from './CancelDataUploadButton';

export type DataUploadsTableProps = {
  data: BulkImport[] | undefined;
};

const SHOW_PRE_PROCESSING_STATS = [
  BulkImportStatus.DATA_PRE_PROCESSED,
  BulkImportStatus.READY_TO_PROCESS,
];

export default function DataUploadsTable({ data }: DataUploadsTableProps) {
  const { isSuperAdmin } = useCurrentUser();
  const showDestroyAllDataDialog = useDestroyAllDataDialog();

  const sortedData = React.useMemo(
    () =>
      data?.sort((a, b) => {
        // sort list on client side by date
        // so most recent upload is at the top
        if (a.createdAt < b.createdAt) {
          return 1;
        }

        if (a.createdAt > b.createdAt) {
          return -1;
        }

        return 0;
      }) ?? [],
    [data],
  );

  const renderStatus = (status: BulkImportStatus, bulkImportId: string) => {
    const statusName = (
      <span className="capitalize">{BulkImportStatusName[status]}</span>
    );

    switch (status) {
      case BulkImportStatus.NEW:
        return (
          <RouterLink
            data-testid="match-headers-link"
            to={generatePath(AuthRoutes.COMPANY_DATA_UPLOADS_MATCH_HEADERS, {
              bulkImportId,
            })}
          >
            {statusName}
          </RouterLink>
        );
      case BulkImportStatus.DATA_PRE_PROCESSED:
      case BulkImportStatus.DATA_PROCESSED:
        return (
          <RouterLink
            data-testid="review-data-summary-link"
            to={generatePath(AuthRoutes.COMPANY_DATA_UPLOADS_REVIEW, {
              bulkImportId,
            })}
          >
            {statusName}
          </RouterLink>
        );
      default:
        return statusName;
    }
  };

  const renderAction = (status: BulkImportStatus, bulkImportId: string) => {
    switch (status) {
      case BulkImportStatus.NEW:
      case BulkImportStatus.HEADERS_MAPPED:
      case BulkImportStatus.PRE_PROCESSING_DATA:
      case BulkImportStatus.DATA_PRE_PROCESSED:
        return (
          <CancelDataUploadButton
            bulkImportId={bulkImportId}
            data-testid="data-review-cancel-button"
            icon
          >
            {translate('DATA_UPLOADS_CANCEL_DATA_UPLOAD')}
          </CancelDataUploadButton>
        );
      case BulkImportStatus.DATA_PROCESSED:
        return isSuperAdmin ? (
          <IconButton
            aria-label={translate('DATA_UPLOADS_DESTROY_ALL_DATA')}
            data-testid="destroy-all-data-button"
            onClick={() => showDestroyAllDataDialog(bulkImportId)}
          >
            <img alt="" src={NuclearIcon} />
          </IconButton>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <TableContainer className="mb-4" data-testid="data-uploads-table">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 150 }}>
              {translate('DATE_LABEL')}
            </TableCell>
            <TableCell>{translate('DATA_UPLOADS_TEAM_MEMBER')}</TableCell>
            <TableCell>{translate('DATA_UPLOADS_FILE_NAME')}</TableCell>
            <TableCell>{translate('DATA_UPLOADS_TOTAL')}</TableCell>
            <TableCell>{translate('DATA_UPLOADS_ERRORS_LABEL')}</TableCell>
            <TableCell>{translate('DATA_UPLOADS_STATUS')}</TableCell>
            <TableCell className="min-w-[80px]" />
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.map(
            ({
              bulkImportId,
              createdAt,
              createdBy,
              inputFileName,
              status,
              processedStats,
              preProcessedStats,
            }) => (
              <TableRow
                className="child:type-body1"
                data-testid={`row-${bulkImportId}`}
                key={bulkImportId}
              >
                <TableCell label={translate('DATE_LABEL')}>
                  <div className="flex flex-col">
                    <span>{format(new Date(createdAt), 'PP')}</span>
                    <span className="text-text-secondary">
                      {new Date(createdAt).toLocaleTimeString('en-US')}
                    </span>
                  </div>
                </TableCell>
                <TableCell label={translate('DATA_UPLOADS_TEAM_MEMBER')}>
                  {createdBy.displayName}
                </TableCell>
                <TableCell label={translate('DATA_UPLOADS_FILE_NAME')}>
                  {inputFileName}
                </TableCell>
                <TableCell label={translate('DATA_UPLOADS_TOTAL')}>
                  {showProcessedStats.includes(status) && (
                    <span data-testid="processing-counts-total">
                      {processedStats?.totals?.records}
                    </span>
                  )}
                  {SHOW_PRE_PROCESSING_STATS.includes(status) && (
                    <span data-testid="pre-processing-counts-total">
                      {preProcessedStats?.totals?.records}
                    </span>
                  )}
                </TableCell>
                <TableCell label={translate('DATA_UPLOADS_ERRORS_LABEL')}>
                  {showProcessedStats.includes(status) && (
                    <span data-testid="processing-counts-error">
                      {processedStats?.totals?.failed}
                    </span>
                  )}
                  {SHOW_PRE_PROCESSING_STATS.includes(status) && (
                    <span data-testid="pre-processing-counts-error">
                      {preProcessedStats?.totals?.failed}{' '}
                    </span>
                  )}
                </TableCell>
                <TableCell label={translate('DATA_UPLOADS_STATUS')}>
                  {renderStatus(status, bulkImportId)}
                </TableCell>
                <TableCell className="min-w-[80px] text-center">
                  {renderAction(status, bulkImportId)}
                </TableCell>
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
